import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button'
import Modal from '@/components/Modal'

import TimeSlot from '../TimeSlot'
import {
  AvailabilityModalContent,
  AvailabilityModalDivider,
  AvailabilityModalFooter,
  AvailabilityModalHero,
  AvailabilityModalInfo,
  AvailabilityModalMedia,
  AvailabilityModalSelection,
  AvailabilityModalSlots,
  AvailabilityModalSlotsSection,
  AvailabilityModalSlotsTitle
} from './AvailabilityModal.styles'

interface AvailabilitySlot {
  time: string
  availability: number
}

interface AvailabilityModalProps {
  title: string
  durationLabel: string
  coverImageUrl: string
  slots: AvailabilitySlot[]
  selectedTime: string | null
  onSelect: (hour: string) => void
  onClose: () => void
}

const AvailabilityModal = ({ title, durationLabel, coverImageUrl, slots, selectedTime, onSelect, onClose }: AvailabilityModalProps) => {
  const { t } = useTranslation()
  const [pendingHour, setPendingHour] = useState<string | null>(selectedTime ?? slots[0]?.time ?? null)

  useEffect(() => {
    setPendingHour(selectedTime ?? slots[0]?.time ?? null)
  }, [selectedTime, slots])

  const handleSelect = () => {
    if (!pendingHour) {
      return
    }

    onSelect(pendingHour)
    onClose()
  }

  return (
    <Modal
      onClose={onClose}
      variant="full"
      showClose={true}
      footer={
        <AvailabilityModalFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('cancel')}
          </Button>
          <AvailabilityModalSelection>
            <span>{t('selectedTime')}:</span>
            <strong>{pendingHour || '--:--'}</strong>
          </AvailabilityModalSelection>
          <Button onClick={handleSelect} disabled={!pendingHour}>
            {t('select')}
          </Button>
        </AvailabilityModalFooter>
      }
    >
      <AvailabilityModalContent>
        <AvailabilityModalHero>
          <AvailabilityModalMedia>
            <img src={coverImageUrl} alt={title} />
          </AvailabilityModalMedia>
          <AvailabilityModalInfo>
            <h3>{title}</h3>
            <p>{durationLabel}</p>
          </AvailabilityModalInfo>
        </AvailabilityModalHero>

        <AvailabilityModalDivider />

        <AvailabilityModalSlotsSection>
          <AvailabilityModalSlotsTitle>{t('selectVisitTime')}</AvailabilityModalSlotsTitle>
          <AvailabilityModalSlots>
            {slots.map(slot => (
              <TimeSlot
                key={slot.time}
                time={slot.time}
                availability={slot.availability}
                active={pendingHour === slot.time}
                onClick={() => setPendingHour(slot.time)}
              />
            ))}
          </AvailabilityModalSlots>
        </AvailabilityModalSlotsSection>
      </AvailabilityModalContent>
    </Modal>
  )
}

export default AvailabilityModal
